import { ICellRendererParams } from "ag-grid-enterprise";
import React from "react";

import { IgTooltip } from "@intelligems/ig-theme/IgTooltip";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { ShopifyProductId } from "@intelligems/ig-utils/shopify-id";
import { Box, Stack, useTheme } from "@mui/material";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { useProductTitle } from "@src/hooks/products";
import { useSelector } from "@src/store";
import {
  getProductPickerData,
  getProductPickerProducts,
} from "@src/store/slices/productPicker";
import { ProductPickerSelectedProduct } from "@src/store/slices/productPicker/types";
import { buildsTestCart } from "@src/store/slices/productPicker/util/productPickerPolicy";
import { openShopifyProduct } from "@src/utils/shopify";

function useVariantCountLabel(data: ProductPickerSelectedProduct | undefined) {
  const pickerData = useSelector(getProductPickerData);
  const isTestCart = buildsTestCart(pickerData?.type);

  const testCartVariantCount = useSelector((state) => {
    if (!isTestCart || !data) {
      return undefined;
    }
    return getProductPickerProducts(state).find((p) => p.id === data.id)
      ?.variants?.length;
  });

  if (!data) {
    return null;
  }

  const total = data.totalVariantCount ?? testCartVariantCount ?? 0;

  if (total <= 1) {
    return null;
  }

  const selected = data.selectedVariantIds?.length ?? total;

  if (isTestCart || selected < total) {
    return `${selected} of ${total} variants selected`;
  }

  return null;
}

export const ProductListText = (
  params: ICellRendererParams & {
    data: ProductPickerSelectedProduct;
  }
) => {
  const { data, context } = params;
  const parentProduct = context?.parentProduct;
  const isVariant = !!parentProduct;
  // A variant row's `data.id` is a ProductVariant gid, which `product(id:)`
  // cannot resolve — a variant inherits its title from the parent product.
  const title = useProductTitle(
    isVariant ? parentProduct?.id : data?.id,
    data?.title
  );

  const org = useGetOrg();
  const theme = useTheme();
  const variantLabel = useVariantCountLabel(data);
  const displayId = data?.id ? new ShopifyProductId(data.id).id : "";
  const linkId = parentProduct?.id
    ? new ShopifyProductId(parentProduct.id).id
    : displayId;

  const onIdClick = () => {
    if (org.data.org.shopId) {
      openShopifyProduct(org.data.org.shopId, linkId);
    }
  };

  return (
    <Stack
      direction="column"
      justifyContent="center"
      sx={{ height: "100%", ml: isVariant ? 0 : 3 }}
    >
      <IgTypography
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {data?.title || title}
      </IgTypography>
      <Box sx={{ display: "inline-flex" }}>
        <IgTooltip title={"Go to this product in Shopify"}>
          <IgTypography
            variant={isVariant ? "body2" : undefined}
            sx={{
              "&:hover": {
                cursor: "pointer",
                textDecoration: "underline",
              },
            }}
            color={theme.palette.text.secondary}
            onClick={onIdClick}
          >
            {variantLabel ?? displayId}
          </IgTypography>
        </IgTooltip>
      </Box>
    </Stack>
  );
};
